import type {
  GroupParentAttributeOptionSet,
  Product,
} from "@/components/configurator/global-option/types";
import { addFilter, type FilterCallback } from "@/extensions/hooks";
import { isSameText, toOptionIds } from "@/rules/helpers";
import type { GlobalOptions } from "@/utils/form-data-manager";

// A body side holds a tray slide or a cutting board, not both: a location option is blocked
// when a side it needs is already used by the other accessory on the body.

const NAMESPACE = "duke/plugins/tray-slide-cutting-board";

// Options with this label stay selectable even when their whole group is disabled
const NONE_LABEL = "None";

type Side = "customer" | "operator";
type Accessory = "tray-slide" | "cutting-board";

interface LocationAttribute {
  accessory: Accessory;
  // Option label -> sides it uses. Labels not listed (e.g. "None") use no side.
  sidesByLabel: Record<string, Side[]>;
  // When no side is left, disable the accessory's whole group except "None" options
  // (otherwise only the blocked location options are disabled)
  disableGroupWhenNoSide?: boolean;
}

// Accessories of the same type never block each other. Tray Slides Cashier is not part of the rule.
const LOCATION_ATTRIBUTES: Record<string, LocationAttribute> = {
  tray_slides_location_cu: {
    accessory: "tray-slide",
    sidesByLabel: { Customer: ["customer"] },
    disableGroupWhenNoSide: true,
  },
  tray_slides_location_op: {
    accessory: "tray-slide",
    sidesByLabel: { Operator: ["operator"] },
    disableGroupWhenNoSide: true,
  },
  cutting_board_location: {
    accessory: "cutting-board",
    sidesByLabel: {
      Customer: ["customer"],
      Operator: ["operator"],
      "Customer and Operator": ["customer", "operator"],
    },
    disableGroupWhenNoSide: true,
  },
};

const optionKey = (attributeCode: string, optionId: unknown) => `${attributeCode}:${optionId}`;

// Sides an option label uses, matched like the other rules (case and surrounding spaces ignored)
const sidesForLabel = (location: LocationAttribute, label: string): Side[] =>
  Object.entries(location.sidesByLabel).find(([sideLabel]) => isSameText(sideLabel, label))?.[1] ??
  [];

interface Blocked {
  // "attributeCode:optionId" of location options that need a side the other accessory uses
  options: Set<string>;
  // Location attributes with no free side left (every side-using option blocked)
  locations: Set<string>;
}

const getBlocked = (picks: GlobalOptions[], optionSet: GroupParentAttributeOptionSet): Blocked => {
  const locationOptions: { attributeCode: string; key: string; accessory: Accessory; sides: Side[] }[] = [];
  optionSet.forEach((group) =>
    group.parents.forEach((parent) =>
      parent.child.forEach(({ attributeCode, labelOptions }) => {
        const location = LOCATION_ATTRIBUTES[attributeCode];
        if (!location) return;
        labelOptions.options.forEach(({ value, label }) =>
          locationOptions.push({
            attributeCode,
            key: optionKey(attributeCode, value),
            accessory: location.accessory,
            sides: sidesForLabel(location, label),
          }),
        );
      }),
    ),
  );

  const pickedKeys = new Set(
    picks.flatMap((pick) => toOptionIds(pick.value).map((id) => optionKey(pick.key, id))),
  );
  const picked = locationOptions.filter((option) => pickedKeys.has(option.key));

  const options = new Set(
    locationOptions
      .filter((option) =>
        picked.some(
          (used) =>
            used.accessory !== option.accessory &&
            used.sides.some((side) => option.sides.includes(side)),
        ),
      )
      .map((option) => option.key),
  );

  const locations = new Set(
    Object.keys(LOCATION_ATTRIBUTES).filter((attributeCode) => {
      const placing = locationOptions.filter(
        (option) => option.attributeCode === attributeCode && option.sides.length,
      );
      return placing.length > 0 && placing.every((option) => options.has(option.key));
    }),
  );

  return { options, locations };
};

// Location attribute of the accessory group a dropdown belongs to, if any
const getGroupLocation = (
  optionSet: GroupParentAttributeOptionSet,
  groupName: string,
  parentName: string,
): string | undefined => {
  for (const group of optionSet.values()) {
    if (group.groupName !== groupName) continue;
    return group.parents
      .get(parentName)
      ?.child.find(({ attributeCode }) => LOCATION_ATTRIBUTES[attributeCode])?.attributeCode;
  }
};

// Body and Global Options dropdowns (and the Global Options tick check), judged against that
// dropdown's own picks: gray out location options on a taken side, or, for accessories set to
// disableGroupWhenNoSide (tray slides), every option of the group but "None" when no side is left.
export const disableBlockedOptions: FilterCallback<"attribute.options"> = (
  options,
  { attributeCode, groupName, parentName, picks, optionSet },
) => {
  const groupLocation = getGroupLocation(optionSet, groupName, parentName);
  if (!groupLocation) return options;
  const blocked = getBlocked(picks, optionSet);
  const wholeGroupBlocked =
    LOCATION_ATTRIBUTES[groupLocation].disableGroupWhenNoSide &&
    blocked.locations.has(groupLocation);
  return options.map((option) => {
    const isBlocked = wholeGroupBlocked
      ? !isSameText(option.label, NONE_LABEL)
      : blocked.options.has(optionKey(attributeCode, option.value));
    return isBlocked ? { ...option, isDisabled: true } : option;
  });
};

// Suggest / Search / Global Search: drop products placed on a blocked location
export const removeBlockedProducts: FilterCallback<"body.products"> = (
  productGroups,
  { picks, optionSet },
) => {
  const blocked = getBlocked(picks, optionSet).options;
  if (!blocked.size) return productGroups;

  const isBlocked = (product: Product) =>
    Object.keys(LOCATION_ATTRIBUTES).some((attributeCode) =>
      toOptionIds(product[attributeCode]).some((id) => blocked.has(optionKey(attributeCode, id))),
    );

  return productGroups
    .map((productGroup) => ({
      ...productGroup,
      products: productGroup.products.filter((product) => !isBlocked(product)),
    }))
    .filter((productGroup) => productGroup.products.length);
};

export const registerTraySlideCuttingBoard = () => {
  const removeOptionFilter = addFilter("attribute.options", NAMESPACE, disableBlockedOptions);
  const removeProductFilter = addFilter("body.products", NAMESPACE, removeBlockedProducts);

  return () => {
    removeOptionFilter();
    removeProductFilter();
  };
};
